// clubs/manifest.js
// Light registry of the app's club types. SSOT for the club switcher's targets
// and for enumerating which clubs a user can access. This is deliberately a
// *static data* registry, NOT a runtime shell engine: Framework7 needs the two
// shells (seller `/` + supplier `/suppliers/`) to be physically static route
// trees with unique tab ids (see the club-parameterized-shell architecture doc
// Section 5 + f7v9-vue3-playbook Section 5 rows 16–21). The manifest only answers "where does
// club X live?" and "what does it look like in the switcher?" — the shells
// themselves are hand-built in routes.js / SupplierBasePage / BasePage.
//
// The Phase-2 swap point is taken (supplier-showroom P2-A, 2026-10-19): the
// club is read from the profile's `supplierStage` (backend
// `App\Enums\Club\SupplierStage`, brief sync/briefs/2026-10-18-supplier-showroom.md),
// with `isSupplier` kept only as the fallback for a session cached before the
// field existed. Every call site passes both through `supplierStageOf`.

/**
 * Where an authenticated user with no membership goes — the welcome / club
 * choice screen. Lives here (not only in routes.js, which re-exports it) so the
 * boot pre-empts can read it without importing the whole route table.
 */
export const ONBOARDING_ENTRY = '/join/';

/**
 * The supplier's waiting screen: the shared under-review screen in its supplier
 * variant (design 2026-10-18 Section 4). The landing of an `interested` lead.
 */
export const SUPPLIER_WAITING_PATH = '/join/review/?club=suppliers';

/**
 * The four supplier stages the profile carries as `supplierStage` — derived on
 * the backend, never stored (L-A): `interested` = a pending application the
 * admin has not opened yet; `showroom` = pending, showroom opened (reads only);
 * `active` = approved (today's supplier, `isSupplier`); `none` = everyone else
 * (a seller, a rejected or blocked applicant, no org).
 */
export const SUPPLIER_STAGES = Object.freeze({
  NONE: 'none',
  INTERESTED: 'interested',
  SHOWROOM: 'showroom',
  ACTIVE: 'active',
});

const KNOWN_STAGES = Object.values(SUPPLIER_STAGES);

/**
 * The one reading of "which supplier stage is this user in?".
 *
 * A known `supplierStage` other than `none` wins. Otherwise `isSupplier === true`
 * means `active` — the fallback for a profile cached before the backend sent the
 * stage (the backend's `isSupplier` is exactly `active`). Anything else is `none`.
 *
 * @param {{ supplierStage?: string, isSupplier?: boolean }} [who]
 * @returns {'none'|'interested'|'showroom'|'active'}
 */
export function supplierStageOf({ supplierStage, isSupplier } = {}) {
  if (KNOWN_STAGES.includes(supplierStage) && supplierStage !== SUPPLIER_STAGES.NONE) {
    return supplierStage;
  }
  return isSupplier === true ? SUPPLIER_STAGES.ACTIVE : SUPPLIER_STAGES.NONE;
}

/**
 * May this user browse the supplier shell? The app's twin of the backend's
 * `SupplierAccess::canBrowseShowroom()` — `showroom` or `active`.
 *
 * @param {{ supplierStage?: string, isSupplier?: boolean }} [who]
 * @returns {boolean}
 */
export function canBrowseSupplierShell(who = {}) {
  const stage = supplierStageOf(who);
  return stage === SUPPLIER_STAGES.SHOWROOM || stage === SUPPLIER_STAGES.ACTIVE;
}

export const CLUB_TYPES = Object.freeze({
  SELLERS: 'sellers',
  SUPPLIERS: 'suppliers',
  COLLECTORS: 'collectors',
});

/** Freeze an engagement block and every object / array inside it. */
function frozenEngagement(block) {
  Object.values(block).forEach((value) => {
    if (value && typeof value === 'object') frozenEngagement(value);
  });
  return Object.freeze(block);
}

/**
 * Per-club static descriptor.
 * - `root`       : the shell's entry URL (switcher navigates here).
 * - `label`      : i18n key for the club's display name (member-facing → ar via poolit-voice).
 * - `description`: i18n key for the switcher row subtitle.
 * - `icon`       : Framework7 icon name for the switcher / tabbar.
 * - `capability` : the auth-store getter that names the club's gate, or `null`
 *                  for the always-present base club. Descriptive only for
 *                  suppliers: `enumerateClubs` picks them by `supplierStageOf`,
 *                  which reads `supplierStage` first and this flag as fallback.
 * - `i18nNs`     : the locale namespace that owns this club's surface strings.
 * - `staff`      : true for a STAFF surface (collectors), not a member club.
 *                  A staff entry is never enumerated, never offered in the
 *                  switcher and never a login landing: it is reached by its URL
 *                  or, for a collector with no club membership, by the Telegram
 *                  launch handoff (`launchLandingPath`). It still owns its URL
 *                  namespace, so `clubTypeForPath` keeps both member tabbars
 *                  and the seller lifecycle banner off it.
 * - `engagement` : how the club is installed and notified (PWA install /
 *                  notifications audit 2026-11-03, Section 3.2). Frozen; read
 *                  it through `engagementFor`, never by club key.
 *     - `install.trigger`     : when the install offer may come up —
 *                               'after-value-moment' | 'after-first-browse' |
 *                               'at-day-open' | 'never'.
 *     - `install.snoozeDays`  : quiet days after a dismiss.
 *     - `install.reaskOnNewMoment` : a dismissed offer may return on the next
 *                               different moment.
 *     - `install.channels`    : the `installChannel()` values
 *                               (platform/capabilities.js) the offer shows on.
 *     - `pushMoments`         : the priming moments this club asks push at.
 *     - `reliableRail`        : the channel that always reaches the user —
 *                               'team-group' | 'bot-dm' | 'collector-group'.
 *     - `notificationsRoot`   : the notification center route, or `null`.
 *     - `welcomePush`         : the `WELCOME_PUSH_COPY` key (constants/push.js),
 *                               or `null` for no welcome push.
 *     - `updatePolicy`        : a waiting service-worker update — 'prompt' (the
 *                               dialog) | 'auto-at-idle'.
 *
 * Insertion order is meaningful: it defines the order clubs appear in the
 * switcher and the order `enumerateClubs` returns (sellers first — the base
 * club — then suppliers).
 */
export const CLUB_MANIFEST = Object.freeze({
  sellers: {
    type: CLUB_TYPES.SELLERS,
    root: '/',
    label: 'supplier.switcher.clubs.sellers.label',
    description: 'supplier.switcher.clubs.sellers.description',
    icon: 'house_fill',
    capability: null,
    i18nNs: 'app',
    engagement: frozenEngagement({
      install: {
        trigger: 'after-value-moment',
        snoozeDays: 7,
        reaskOnNewMoment: true,
        channels: ['android-prompt', 'ios-safari', 'desktop'],
      },
      pushMoments: ['orderbox', 'deposit', 'registration', 'profile_status'],
      reliableRail: 'team-group',
      notificationsRoot: '/notifications/',
      welcomePush: 'sellers',
      updatePolicy: 'prompt',
    }),
  },
  suppliers: {
    type: CLUB_TYPES.SUPPLIERS,
    root: '/suppliers/',
    label: 'supplier.switcher.clubs.suppliers.label',
    description: 'supplier.switcher.clubs.suppliers.description',
    icon: 'cube_box_fill',
    capability: 'isSupplier',
    i18nNs: 'supplier',
    engagement: frozenEngagement({
      install: {
        trigger: 'after-first-browse',
        snoozeDays: 7,
        reaskOnNewMoment: true,
        channels: ['android-prompt', 'ios-safari', 'desktop'],
      },
      // 'registration' is deliberately NOT a supplier moment: the supplier's
      // ask at registration is "let the assistant bot message you" (decision
      // D4), not the seller push soft-ask.
      pushMoments: ['profile_status', 'claim', 'box_confirmed', 'withdrawal'],
      reliableRail: 'bot-dm',
      notificationsRoot: '/notifications/',
      welcomePush: 'suppliers',
      updatePolicy: 'prompt',
    }),
  },
  // The collector's day (collector-app phase 1). Staff, not a club: a leader or
  // the admin who is also a member keeps their club landing and types the URL;
  // only a collector with NO club membership lands here, through LaunchPage.
  collectors: {
    type: CLUB_TYPES.COLLECTORS,
    root: '/collector/',
    label: 'collector.day.title',
    description: 'collector.day.title',
    icon: 'cart_fill',
    capability: 'isCollector',
    i18nNs: 'collector',
    staff: true,
    // No push and no notification center: the collector group is the rail.
    // A waiting update applies itself when the day is idle, never a dialog
    // over a shop visit (decision D3).
    engagement: frozenEngagement({
      install: {
        trigger: 'at-day-open',
        snoozeDays: 7,
        reaskOnNewMoment: true,
        channels: ['android-prompt', 'ios-safari', 'desktop'],
      },
      pushMoments: [],
      reliableRail: 'collector-group',
      notificationsRoot: null,
      welcomePush: null,
      updatePolicy: 'auto-at-idle',
    }),
  },
});

/** The member clubs — every manifest entry that is not a staff surface. */
const memberClubs = () => Object.values(CLUB_MANIFEST).filter((club) => club.staff !== true);

/**
 * Which club is this user in?
 *
 * ONE CLUB PER ORG is a hard invariant (BE 2026-06-20 locked framing) — seller
 * and supplier memberships are mutually exclusive — so this returns a
 * SINGLE-element array, never a stack. Any supplier stage other than `none`
 * (interested, showroom, active) is the suppliers club — a supplier lead never
 * belongs in the seller shell, even before the admin opens his showroom — else
 * the base club (sellers).
 *
 * @param {{ supplierStage?: string, isSupplier?: boolean }} capabilities -
 *   typically `{ supplierStage: authStore.supplierStage, isSupplier: authStore.isSupplier }`.
 * @returns {string[]} a single-element array of the user's club type.
 */
export function enumerateClubs(capabilities = {}) {
  if (supplierStageOf(capabilities) !== SUPPLIER_STAGES.NONE) return [CLUB_TYPES.SUPPLIERS];
  const base = memberClubs().find((club) => club.capability === null);
  return base ? [base.type] : [];
}

/**
 * The post-auth landing path for an authenticated member, by club and supplier
 * stage. SSOT for "where does this member belong?" — shared by the guards
 * (`redirectIfAuthenticated`, `requireMembership`, `requireSupplier`,
 * `redirectMembersToShell`) and the boot-time pre-empts in App.vue so they
 * never drift.
 *
 *  - `interested` → the waiting screen (`SUPPLIER_WAITING_PATH`).
 *  - `showroom` | `active` → the supplier shell root.
 *  - `none` → `/home/`, the established seller-shell entry. Note this is the
 *    redirect alias, NOT `CLUB_MANIFEST.sellers.root` ('/') directly: `/home/`
 *    is a redirect route with no `component`, which forces F7 down the
 *    `router.navigate()` path so `requireMembership` actually runs (an eager
 *    `component:` whose page renders an `<f7-page>` would skip the guard on the
 *    initial page — see f7v9-vue3-playbook Section 5).
 *
 * @param {{ supplierStage?: string, isSupplier?: boolean }} capabilities
 * @returns {string} the landing route path, e.g. '/home/' or '/suppliers/'.
 */
export function clubLandingPath(capabilities = {}) {
  const stage = supplierStageOf(capabilities);
  if (stage === SUPPLIER_STAGES.INTERESTED) return SUPPLIER_WAITING_PATH;
  if (stage === SUPPLIER_STAGES.NONE) return '/home/';
  return CLUB_MANIFEST.suppliers.root;
}

/**
 * Where the Telegram → browser launch handoff (LaunchPage) sends a signed-in
 * user. A collector with NO club membership lands on the collector's day: the
 * Telegram-only collectors reach Android Chrome through that handoff, and
 * `/home/` would bounce them to onboarding (collector-app phase 2 research,
 * Section 2). Everyone else — a leader or the admin who is also a member
 * included — keeps `/home/`, whose own guard routes a supplier on.
 *
 * @param {{ isCollector?: boolean, hasActiveMembership?: boolean }} who
 * @returns {string} '/collector/' or '/home/'.
 */
export function launchLandingPath({ isCollector = false, hasActiveMembership = false } = {}) {
  return isCollector === true && hasActiveMembership !== true
    ? CLUB_MANIFEST.collectors.root
    : '/home/';
}

/**
 * How this user's club is installed and notified: the frozen `engagement`
 * block of the collectors' staff surface when the launch handoff would land
 * them there (a collector with no club membership, `launchLandingPath`), else
 * of the one club `enumerateClubs` resolves to. A leader or the admin who is
 * also a member gets their club's block.
 *
 * @param {{ supplierStage?: string, isSupplier?: boolean, isCollector?: boolean, hasActiveMembership?: boolean }} [who]
 *   typically read off `useAuthStore()`.
 * @returns {Readonly<object>} the club's `engagement` block.
 */
export function engagementFor({
  supplierStage,
  isSupplier,
  isCollector,
  hasActiveMembership,
} = {}) {
  if (launchLandingPath({ isCollector, hasActiveMembership }) === CLUB_MANIFEST.collectors.root) {
    return CLUB_MANIFEST.collectors.engagement;
  }
  const [club] = enumerateClubs({ supplierStage, isSupplier });
  return CLUB_MANIFEST[club].engagement;
}

/**
 * Derive the active club type from a route path/URL. The URL namespace *is*
 * the context — no separate state machine. Anything under `/suppliers/` (the
 * shell root or any of its tabs) is the suppliers club, anything under
 * `/collector/` the collector's staff surface; everything else is the sellers
 * club.
 *
 * @param {string} [path] - an F7 route URL, e.g. '/suppliers/claims/'.
 * @returns {'sellers'|'suppliers'|'collectors'}
 */
export function clubTypeForPath(path) {
  if (typeof path !== 'string') return CLUB_TYPES.SELLERS;
  for (const club of [CLUB_MANIFEST.suppliers, CLUB_MANIFEST.collectors]) {
    const { root } = club; // e.g. '/suppliers/'
    const slashless = root.slice(0, -1); // e.g. '/suppliers'
    if (path === slashless || path.startsWith(root)) return club.type;
  }
  return CLUB_TYPES.SELLERS;
}
